import moment from "moment";
import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Modal,
  ProgressBar,
  Row,
} from "react-bootstrap";
import {
  Calendar2,
  CheckCircleFill,
  Circle,
  Pen,
  Trash,
} from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { makeRESTApiRequests } from "../../helpers/api";
import { ENDPOINTS } from "../../helpers/constants";
import CircularProgressBar from "../circular-progress";

const addressFields = [
  "Property Address",
  "City",
  "County",
  "State",
  "Zip Code",
];

const TaskSearchItem = ({
  task,
  onStatusUpdate,
  onEditClick,
  onTaskDelete,
  taskIndex,
  tasks = [],
  jobId,
}) => {
  const [status, setStatus] = useState(task["status"] || "Incomplete");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [deletingTask, setDeletingTasks] = useState(false);

  const updateStatus = async (s) => {
    if (taskIndex === -1) return;

    setShowConfirmationModal(false);

    if (status === s) return;

    setUpdatingStatus(true);

    let newTask = {
      ...task,
      status: s,
    };

    let requestTasks = tasks ? [...tasks] : [];
    if (taskIndex !== -1) {
      requestTasks[taskIndex] = newTask;
    }

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.UPDATE_INDIVIDUAL_JOB(jobId),
      method: "PUT",
      requestBody: { tasks: requestTasks },
    });

    setUpdatingStatus(true);

    if (error) {
      toast(error, {
        type: "error",
      });
      return;
    }

    if (response) {
      toast(`Status updated successfully!`, {
        type: "success",
      });

      setStatus(s);
      setUpdatingStatus(false);
      onStatusUpdate && onStatusUpdate(s, taskIndex);
    }
  };

  const deleteTask = async () => {
    if (taskIndex === -1) return;

    setDeletingTasks(true);

    let requestTasks = [...tasks];
    requestTasks.splice(taskIndex, 1);

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.UPDATE_INDIVIDUAL_JOB(jobId),
      method: "PUT",
      requestBody: { tasks: requestTasks },
    });

    setDeletingTasks(false);

    if (error) {
      toast(error, {
        type: "error",
      });
      return;
    }
    if (response) {
      toast(`Task deleted successfully!`, {
        type: "success",
      });

      setDeletingTasks(false);
      setShowConfirmationModal(false);
      onTaskDelete && onTaskDelete(taskIndex);
    }
  };

  const ConfirmationModal = () => {
    return (
      <>
        <Modal
          show={showConfirmationModal}
          onHide={() => setShowConfirmationModal(false)}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton={!deletingTask}>
            <Modal.Title>Are you sure?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Do you really want to{" "}
            {showConfirmationModal.type === "delete"
              ? "delete this task. This action cannot be reverted."
              : "mark this task as complete?"}
            {deletingTask && (
              <ProgressBar
                variant="dark"
                className="my-2"
                label="Deleting task..."
                animated
                now={100}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Row>
              <Col>
                <Button
                  disabled={deletingTask}
                  variant="secondary"
                  onClick={() => setShowConfirmationModal(false)}
                  className="mr-2"
                >
                  No
                </Button>
                <Button
                  disabled={deletingTask}
                  variant="danger"
                  onClick={() => {
                    if (showConfirmationModal.type === "delete") {
                      deleteTask();
                    } else {
                      updateStatus("Complete");
                    }
                  }}
                >
                  Yes
                </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Modal>
      </>
    );
  };

  return (
    <Card className="h-100">
      <Card.Body className="p-2">
        <div className="d-flex gap-2">
          <div className="text-center align-self-center">
            {updatingStatus ? (
              <CircularProgressBar size={2} />
            ) : (
              <>
                {status === "Complete" ? (
                  <CheckCircleFill
                    size={22}
                    className="text-success hover"
                    onClick={() => updateStatus("Incomplete")}
                  />
                ) : (
                  <>
                    <Circle
                      size={22}
                      className="text-secondary hover"
                      onClick={() =>
                        setShowConfirmationModal({ type: "status" })
                      }
                    />
                  </>
                )}
              </>
            )}
          </div>

          <div className={`flex-grow-1 pl-2`}>
            <div className="d-flex flex-wrap">
              <div className="flex-grow-1">
                <Badge
                  style={styles.smallerFont}
                  variant="info"
                  className="py-1"
                >
                  <Calendar2 className="align-text-top mr-1" size={12} />{" "}
                  {moment(task["dueDate"]).format("MMMM Do YYYY")}
                </Badge>
              </div>
              <div className="d-flex mt-1">
                <Button
                  size="sm"
                  variant="success"
                  className="py-0"
                  onClick={onEditClick}
                >
                  <Pen style={{ verticalAlign: "baseline" }} size={10} />
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  className="ml-2 py-0"
                  onClick={() => setShowConfirmationModal({ type: "delete" })}
                >
                  <Trash style={{ verticalAlign: "baseline" }} size={10} />
                </Button>
              </div>
            </div>
            <hr className="my-2" />
            <Row className="mt-2">
              <Col xs={12}>
                <Alert
                  className="mb-0 w-auto -100 text-dark p-1"
                  variant="primary"
                  style={styles.smallFont}
                >
                  {task["description"]}
                </Alert>
              </Col>
            </Row>
          </div>
        </div>
      </Card.Body>
      {ConfirmationModal()}
    </Card>
  );
};

const styles = {
  smallFont: {
    fontSize: 14,
  },
  smallerFont: {
    fontSize: 10,
  },
  smallestFont: {
    fontSize: 10,
  },
};

export default TaskSearchItem;
